import { Fragment, useState } from 'react';
import { can, formatRial, tehranYm, toFaDigits } from '@kia/shared';
import { readBase64 } from '../ai/attachments';
import { api, getAuthToken } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { rowButton } from '../lib/rowButton';
import { useApiResource } from '../lib/useApiResource';
import { Button, Card, ErrorRetry, Field, Input, NumberInput, useToast } from '../ui';

/**
 * هزینه‌های ثابت / تعهدات دوره‌ای (CIRCULARITY_360 Phase B2) — monthly templates (payroll, rent,
 * utilities, overhead) that project as ESTIMATED outflows on the cash calendar. Logging the real
 * payment (with an optional proof photo) stops that month projecting and records the actual, so the
 * estimate→actual→variance loop can refine the template. When any template is active, the calendar
 * uses templates instead of the blunt weighted-average opex tail.
 */

const CATEGORIES: [key: string, label: string][] = [
  ['payroll', 'حقوق و دستمزد'],
  ['rent', 'اجاره'],
  ['utilities', 'قبوض (آب/برق/گاز/تلفن)'],
  ['overhead', 'سربار'],
  ['tax', 'مالیات'],
  ['other', 'سایر'],
];
const catLabel = (k: string) => CATEGORIES.find((c) => c[0] === k)?.[1] ?? k;

interface Obligation {
  id: number;
  category: string;
  label: string;
  est_amount: number;
  day_of_month: number;
  active: number;
  lastActual: { ym: string; amount: number } | null;
  recentAvg: number | null;
  variancePct: number | null;
}
interface Actual {
  id: number;
  ym: string;
  amount: number;
  paid_on: string | null;
  proof_doc_id: number | null;
}

// Tehran calendar month — NOT raw UTC `toISOString()`, which in the Tehran evening rolls to the previous
// month and would log an actual against the wrong period (audit H2/DT-H2).
function currentYm(): string {
  return tehranYm();
}

export function RecurringPanel() {
  const { user } = useAuth();
  const toast = useToast();
  const canWrite = user ? can(user.role, 'cashflow.write') : false;
  const { data, status, error, reload } = useApiResource<Obligation[]>('/api/recurring');
  const obs = data ?? [];
  const [openId, setOpenId] = useState<number | null>(null);
  const [actuals, setActuals] = useState<Actual[]>([]);
  const [adding, setAdding] = useState(false);

  // add-template form
  const [category, setCategory] = useState('payroll');
  const [label, setLabel] = useState('');
  const [est, setEst] = useState(0);
  const [day, setDay] = useState(28);
  const [busy, setBusy] = useState(false);

  // log-actual form (per open obligation)
  const [aYm, setAYm] = useState(currentYm());
  const [aAmount, setAAmount] = useState(0);
  const [aFile, setAFile] = useState<File | null>(null);

  async function openRow(o: Obligation) {
    if (openId === o.id) {
      setOpenId(null);
      return;
    }
    setOpenId(o.id);
    setAYm(currentYm());
    setAAmount(o.est_amount);
    setAFile(null);
    const r = await api<Actual[]>(`/api/recurring/${o.id}/actuals`);
    if (r.ok) setActuals(r.body as Actual[]);
  }

  async function submitTemplate() {
    if (!label.trim() || !(est >= 0)) {
      toast('عنوان و مبلغ لازم است', 'error');
      return;
    }
    setBusy(true);
    const r = await api('/api/recurring', {
      method: 'POST',
      body: JSON.stringify({ category, label, est_amount: est, day_of_month: day }),
    });
    setBusy(false);
    if (!r.ok) return toast('خطا در ثبت', 'error');
    toast('تعهد ثبت شد ✓');
    setAdding(false);
    setLabel('');
    setEst(0);
    reload();
  }

  async function toggleActive(o: Obligation) {
    const r = await api(`/api/recurring/${o.id}`, {
      method: 'POST',
      body: JSON.stringify({ active: o.active ? 0 : 1 }),
    });
    if (!r.ok) return toast('خطا در تغییر وضعیت', 'error');
    toast(o.active ? 'تعهد غیرفعال شد' : 'تعهد فعال شد');
    reload();
  }

  async function logActual(o: Obligation) {
    if (!(aAmount >= 0)) return toast('مبلغ نامعتبر', 'error');
    setBusy(true);
    const proof = aFile
      ? { filename: aFile.name, mime: aFile.type, dataBase64: await readBase64(aFile) }
      : undefined;
    const r = await api(`/api/recurring/${o.id}/actual`, {
      method: 'POST',
      // Stamp the actual on the obligation's OWN scheduled day, not a hardcoded 15th (audit H3) — else every
      // actual lands mid-month and corrupts the cash calendar's day-bucketing.
      body: JSON.stringify({
        ym: aYm,
        amount: aAmount,
        paid_on: `${aYm}-${String(o.day_of_month).padStart(2, '0')}`,
        proof,
      }),
    });
    setBusy(false);
    if (!r.ok)
      return toast((r.body as { message?: string }).message ?? 'خطا در ثبت پرداخت', 'error');
    toast('پرداخت ثبت شد ✓');
    setAFile(null);
    const a = await api<Actual[]>(`/api/recurring/${o.id}/actuals`);
    if (a.ok) setActuals(a.body as Actual[]);
    reload();
  }

  return (
    <Card title="هزینه‌های ثابت و تعهدات دوره‌ای">
      <p className="muted" style={{ marginTop: 0 }}>
        حقوق، اجاره، قبوض و سربار به‌صورت برآورد ماهانه در «تقویم نقدینگی» لحاظ می‌شوند. با ثبت
        پرداخت واقعی هر ماه (همراه عکس رسید)، برآورد آن ماه با رقم واقعی جایگزین و از پیش‌بینی حذف
        می‌شود.
      </p>

      {canWrite ? (
        adding ? (
          <div
            className="row"
            style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}
          >
            <Field label="دسته">
              <select
                className="input"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORIES.map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="عنوان">
              <Input value={label} onChange={(e) => setLabel(e.target.value)} />
            </Field>
            <Field label="برآورد ماهانه (﷼)">
              <div style={{ width: 170 }}>
                <NumberInput value={est} onValue={setEst} />
              </div>
            </Field>
            <Field label="روز پرداخت (ماه)">
              <div style={{ width: 90 }}>
                <NumberInput value={day} onValue={setDay} />
              </div>
            </Field>
            <Button onClick={submitTemplate} loading={busy}>
              ثبت
            </Button>
            <Button variant="secondary" onClick={() => setAdding(false)} disabled={busy}>
              انصراف
            </Button>
          </div>
        ) : (
          <Button variant="secondary" onClick={() => setAdding(true)} style={{ marginBottom: 12 }}>
            + تعهد جدید
          </Button>
        )
      ) : null}

      {status === 'error' ? (
        <ErrorRetry error={error} onRetry={() => reload()} />
      ) : status === 'loading' ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : obs.length === 0 ? (
        <p className="muted">تعهد دوره‌ای ثبت نشده.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>عنوان</th>
                <th>دسته</th>
                <th>برآورد ماهانه</th>
                <th>روز</th>
                <th>میانگین اخیر</th>
                <th>انحراف</th>
                <th>وضعیت</th>
              </tr>
            </thead>
            <tbody>
              {obs.map((o) => (
                <Fragment key={o.id}>
                  <tr
                    {...rowButton(() => openRow(o))}
                    style={{ cursor: 'pointer', opacity: o.active ? 1 : 0.5 }}
                  >
                    <td>
                      {openId === o.id ? '▾ ' : '▸ '}
                      {o.label}
                    </td>
                    <td>{catLabel(o.category)}</td>
                    <td className="num">{formatRial(o.est_amount)}</td>
                    <td>{toFaDigits(String(o.day_of_month))}</td>
                    <td className="num">{o.recentAvg != null ? formatRial(o.recentAvg) : '—'}</td>
                    <td
                      className="num"
                      style={{
                        color:
                          o.variancePct == null
                            ? undefined
                            : o.variancePct > 0
                              ? 'var(--red)'
                              : 'var(--green)',
                      }}
                    >
                      {o.variancePct == null
                        ? '—'
                        : `${o.variancePct > 0 ? '+' : ''}${toFaDigits((o.variancePct * 100).toFixed(0))}٪`}
                    </td>
                    <td>{o.active ? 'فعال' : 'غیرفعال'}</td>
                  </tr>
                  {openId === o.id ? (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--panel, #fafafa)' }}>
                        {canWrite ? (
                          <div
                            className="row"
                            style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}
                          >
                            <Field label="ماه">
                              <Input
                                type="month"
                                value={aYm}
                                onChange={(e) => setAYm(e.target.value)}
                              />
                            </Field>
                            <Field label="مبلغ واقعی (﷼)">
                              <div style={{ width: 160 }}>
                                <NumberInput value={aAmount} onValue={setAAmount} />
                              </div>
                            </Field>
                            <Field label="عکس رسید (اختیاری)">
                              <input
                                className="input"
                                type="file"
                                accept="image/*,application/pdf"
                                onChange={(e) => setAFile(e.target.files?.[0] ?? null)}
                              />
                            </Field>
                            <Button onClick={() => logActual(o)} loading={busy}>
                              ثبت پرداخت
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={() => toggleActive(o)}
                              disabled={busy}
                            >
                              {o.active ? 'غیرفعال‌سازی' : 'فعال‌سازی'}
                            </Button>
                          </div>
                        ) : null}
                        {actuals.length ? (
                          <table className="table" style={{ margin: '10px 0 0' }}>
                            <thead>
                              <tr>
                                <th>ماه</th>
                                <th>مبلغ واقعی</th>
                                <th>رسید</th>
                              </tr>
                            </thead>
                            <tbody>
                              {actuals.map((a) => (
                                <tr key={a.id}>
                                  <td>{toFaDigits(a.ym)}</td>
                                  <td className="num">{formatRial(a.amount)}</td>
                                  <td>
                                    {a.proof_doc_id ? (
                                      <a
                                        href={`/api/documents/${a.proof_doc_id}/raw?t=${getAuthToken() ?? ''}`}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        📎 مشاهده
                                      </a>
                                    ) : (
                                      '—'
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="muted" style={{ margin: '8px 0 0' }}>
                            هنوز پرداختی برای این تعهد ثبت نشده.
                          </p>
                        )}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
